import {
  performOnlineItemAuthorization,
  showAuthorizingModal,
  showOfflineGiftCardActivationModal,
} from 'src/brm/actions/authorize/authorize-online-items';
import { showEmeraldSelectionDialog } from 'src/common/components/interactive-dialogue/components/selection-dialogue/selection-dialog';
import { SelectListItem } from 'src/common/components/select-list/select-list';
import { AuthorizationStatus } from 'src/common/enumerations/authorization-status';
import {
  PaidInManagerApproval,
  PaidInProhibhitBeyondBoundryamount,
  ProhibitOverTenderRestrictionBehavior,
} from 'src/common/helpers/error-code';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { store } from 'src/common/redux/configure-store';
import { voidTransactionWhenUEOffline } from 'src/home-page/business-logic/void-transaction';
import { handleItemNotFoundIntervention } from 'src/item-not-found/business-logic/handle-item-not-found';
import { RestrictionType } from 'src/items-list/business-logic/service/enumerations/cart-restrictions-types';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import { CartItemActivation } from 'src/online-items/dto/CartItemActivation';
import { showInputDataRequiredYesNoDialog } from '../../../brm/actions/input-data-required-yesno/input-data-required-yesno';
import { showInputDataRequiredDialog } from '../../../brm/actions/input-data-required/input-data-required';
import { showSupervisorApprovalDialog } from '../../../brm/actions/supervisor-approval/supervisor-approval';
import { popupAsyncMessage } from '../../../messages/business-logic/popup-message';
import { showEmeraldDialog } from '../../components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { popEmeraldModal } from '../../emerald-modal-manager/emerald-modal-manager';
import { BrmActionType } from '../../enumerations/brm-action-type';
import { EmeraldDialogTypes } from '../../enumerations/emerald-dialog-types';
import * as EmeraldErrorCodes from '../../helpers/error-code';
import { getErrorMessage } from '../../helpers/handle-supervisor-actions-helper';
import { IReasonCode } from '../../interfaces/dtos/IReasonCode';
import { EmeraldError } from '../emerald-error';
import { AbortedError, ProhibitActionError } from '../extended-emerald-error';
import {
  AgeRestrictionApprovalType,
  handleAgeRestrictionBRM,
} from './age-restriction/age-restriction-handler';

let modalId: string | undefined = undefined;

export interface ApprovalCredentialInput {
  userName?: string;
  password: string;
  barcode?: string;
}

export interface IBRMInput {
  ruleId: string;
  type: BrmActionType;
  lineId?: string;
  restrictionType?: string;
  key?: string;
  inputKey?: string;
  data?:
    | string
    | ApprovalCredentialInput
    | IBRMAgeRestrictionInput
    | IBRMInputDataRequiredInput
    | CartItemActivation;
}

export interface IBRMInputDataRequiredInput {
  fieldName: string | undefined;
  value: string;
}
export interface IBRMAgeRestrictionInput {
  approvalType?: AgeRestrictionApprovalType;
  subAspectType?: string;
  isApproved?: boolean;
  customerBirthDate?: string;
  rejectReasonCode?: string;
  scanData?: string;
  action?: ICustomerAgeAction;
}

export interface IBRMBaseRule {
  // The rule id
  ruleId: string;
  // The BRM Action type, typically the required action from the UI
  type: BrmActionType;
  // The message associated with the BRM Rule
  message?: IBRMMessage;
  // The BRM restriction's line id (not used by all implementations)
  lineId?: string;
  // The restriction's associated type, typically the action or operation that will trigger the rule
  restrictionType?: string;
  // The BRM restriction's restricted line id's (not used by all implementations)
  restrictedLines?: string[];
  data?: ILoginUser;
}

export interface IBRMRule extends IBRMBaseRule {
  input?: {
    type: DataInputs;
    inputName: string;
    inputLabel: string;
  };
  validationError?: string;
  reasons?: IReasonCode[];
  isDelayedSolution?: boolean;
  requiresAttendant?: boolean;
  rejectedReason?: ApprovalRejectReason;
}

export enum ApprovalRejectReason {
  NotAuthorized = 'NotAuthorized',
  ApprovalDataMissing = 'ApprovalDataMissing',
}

interface IApprovalAction extends IBRMBaseRule {
  actionId: string;
  errorMessage?: string;
  voidTransactionOnDecline: boolean;
  needRejectedInfoWindow?: boolean;
  restrictedItems?: string[];
}

export interface ICustomerAgeAction extends IBRMBaseRule {
  approveRejectRequired: boolean;
  birthDateRequired: boolean;
  key?: string;
  subAspectType?: string;
  errorMessage?: string;
  reasons?: IReasonCode[];
  scanDriversLicense?: boolean;
  expectedAge?: string;
  itemId?: string;
  needRejectedInfoWindow?: boolean;
  voidTransactionOnDecline?: boolean;
}

export enum DataInputs {
  Date = 'Date',
  Text = 'Text',
  YesNo = 'YesNo',
  Numeric = 'Numeric',
  Birthday = 'Birthday',
}

export interface IBRMMessage {
  caption: string;
  message: string;
}

export const parseBRMActions = (error: EmeraldError): IBRMRule[] | undefined => {
  if (error.code === EmeraldErrorCodes.BRM) {
    return error.data;
  }
};

export const dispatchNotification = async (rule: IBRMRule): Promise<void> => {
  const message = rule.message;
  const title = message ? message.caption : '';
  const text = message ? message.message : '';
  return popupAsyncMessage({ message: title, type: EmeraldDialogTypes.Info, subMessage: text });
};

export const dispatchActions = async (
  brmActions: IBRMRule[],
  depth: number,
): Promise<IBRMInput[]> => {
  const span = createSpan('handle-brm-user-input');
  const brmInputs: IBRMInput[] = [];
  for (const action of brmActions) {
    brmInputs.push(await dispatchAction(action, depth));
  }
  endSpan(span);
  return brmInputs;
};

let needAgeRestrictionRejectInfoWindow: boolean = true;
export const dispatchAction = async (rule: IBRMRule, depth: number): Promise<IBRMInput> => {
  return new Promise((resolve, reject) => {
    const input: IBRMInput = {
      ruleId: rule.ruleId,
      type: rule.type,
      lineId: rule.lineId,
      restrictionType: rule.restrictionType,
    };

    const message = rule.message;
    const title = message ? message.caption : '';
    const text = message ? message.message : '';

    const onCancel = (): void => {
      if (rule.ruleId === PaidInManagerApproval) {
        reject(new EmeraldError(rule.ruleId));
      } else {
        reject(new AbortedError());
      }
      popEmeraldModal(modalId);
      modalId = undefined;
    };

    const onAcceptString = (data: string | IBRMInputDataRequiredInput): void => {
      input.data = data;
      runApproval();
    };

    const onRuleInputApproval = (userName: string, password: string): void => {
      input.data = { userName, password };
      runApproval();
    };

    const onActivateApproval = (
      restrictedLine: string,
      activationData: CartItemActivation,
    ): void => {
      input.lineId = restrictedLine;
      input.data = activationData;
      runApproval();
    };

    const onRetrictionApproval = (userName: string, password: string): void => {
      input.data = { userName, password };
      input.key = (rule as IApprovalAction).actionId;
      runApproval();
    };

    const onAgeRestrictionAccept = (brmInputs?: {
      approvalType?: AgeRestrictionApprovalType;
      isApproved?: boolean;
      rejectReasonCode?: string;
      customerAge?: string;
    }): void => {
      input.data = {
        ...brmInputs,
        subAspectType: (rule as ICustomerAgeAction).subAspectType,
        action: rule as ICustomerAgeAction,
      };
      input.key = (rule as ICustomerAgeAction).key;
      if (
        brmInputs?.approvalType === AgeRestrictionApprovalType.Rejected &&
        brmInputs.isApproved === false
      ) {
        needAgeRestrictionRejectInfoWindow = false;
      }
      runApproval();
    };

    const runApproval = (): void => {
      popEmeraldModal(modalId);
      if (input.data && typeof input.data !== 'string') {
        const data = input.data as CartItemActivation;
        if (
          data.additionalData &&
          data.additionalData.activation?.activationData?.authorizationStatus ===
            AuthorizationStatus.Offline
        ) {
          showOfflineGiftCardActivationModal();
        }
      }
      modalId = undefined;
      resolve(input);
    };

    const onRuleInputBarcodeScanned = async (
      barcode: string,
      password: string = ' ',
    ): Promise<void> => {
      input.data = { barcode, password };
      runApproval();
    };

    const onRestrictionBarcodeScanned = async (
      barcode: string,
      password: string = ' ',
    ): Promise<void> => {
      input.data = { barcode, password };
      input.key = (rule as IApprovalAction).actionId;
      runApproval();
    };
    switch (rule.type) {
      case BrmActionType.OnlineItemAuthorization: {
        if (rule.restrictedLines && rule.restrictedLines.length > 0) {
          showAuthorizingModal();
          performOnlineItemAuthorization(rule.restrictedLines[0], onActivateApproval);
        } else {
          throw new EmeraldError('OnlineItemActivation_Error', `Unable to find restricted item`);
        }
        break;
      }
      case BrmActionType.ApprovalAction: {
        modalId = 'BRM_Approval_Modal';
        showSupervisorApprovalDialog(
          {
            brmMessage: rule.message,
            errorMessage: rule.validationError,
            onAccept: onRuleInputApproval,
            onCancel,
            rule,
            depth,
          },
          onRuleInputBarcodeScanned,
          modalId,
        );
        break;
      }
      case BrmActionType.RestrictionApprovalAction: {
        const approvalAction = rule as IApprovalAction;
        showSupervisorApprovalDialog(
          {
            brmMessage: rule.message,
            errorMessage: getErrorMessage(approvalAction.errorMessage || ''),
            onAccept: onRetrictionApproval,
            onCancel,
            depth,
          },
          onRestrictionBarcodeScanned,
        );
        break;
      }
      case BrmActionType.AgeRestrictionAction: {
        const ageRestrictionRule = rule as ICustomerAgeAction;
        handleAgeRestrictionBRM(ageRestrictionRule, onAgeRestrictionAccept, onCancel);
        break;
      }
      case BrmActionType.ItemNotFound: {
        handleItemNotFoundIntervention(runApproval);
        break;
      }
      case BrmActionType.PriceEntryRequired: {
        runApproval();
        break;
      }
      case BrmActionType.RestrictionProhibitAction:
      case BrmActionType.ProhibitAction: {
        const prohibitRule = rule as IApprovalAction;
        let code = title;
        let needRejectedInfoWindow: boolean;
        const codeSplitByHyphen = rule.ruleId.split('_');
        if (rule.ruleId === PaidInProhibhitBeyondBoundryamount) {
          code = PaidInProhibhitBeyondBoundryamount;
        } else if (
          codeSplitByHyphen.length > 1 &&
          codeSplitByHyphen[1] === ProhibitOverTenderRestrictionBehavior
        ) {
          code = ProhibitOverTenderRestrictionBehavior;
        }
        if (prohibitRule.needRejectedInfoWindow !== undefined) {
          needRejectedInfoWindow = prohibitRule.needRejectedInfoWindow;
        } else {
          needRejectedInfoWindow = true;
          if (
            prohibitRule.restrictionType === 'AgeRestriction' ||
            (prohibitRule.type === BrmActionType.RestrictionProhibitAction &&
              prohibitRule.actionId === 'CustomerAge')
          ) {
            if (needAgeRestrictionRejectInfoWindow === false) {
              needRejectedInfoWindow = false;
              needAgeRestrictionRejectInfoWindow = true;
            } else {
              needRejectedInfoWindow = needAgeRestrictionRejectInfoWindow;
            }
          } else if (prohibitRule.restrictionType === 'ProhibitTender') {
            code = ProhibitOverTenderRestrictionBehavior;
          }
        }
        throw new ProhibitActionError(
          code,
          text,
          needRejectedInfoWindow,
          prohibitRule.restrictedItems,
          prohibitRule.voidTransactionOnDecline,
        );
      }
      case BrmActionType.LoyaltyAction: {
        showInputDataRequiredDialog({
          type: rule.input ? rule.input.type : DataInputs.Text,
          caption: title,
          message: text || '',
          onAccept: onAcceptString,
          onCancel,
        });
        break;
      }
      case BrmActionType.ReasonCodeAction: {
        if (rule.reasons) {
          const selectListItems = rule.reasons.map(
            (r: IReasonCode): SelectListItem => ({
              id: r.value,
              description: r.description,
            }),
          );
          showEmeraldSelectionDialog({
            selectListItems,
            message: rule.message?.caption,
            subMessage: rule.message?.message,
            onCancel,
            onAccept: onAcceptString,
            dataTestId: 'reason-code-required',
          });
        }
        break;
      }
      case BrmActionType.InputDataAction: {
        const type = rule.input ? rule.input.type : DataInputs.Text;

        if (type === DataInputs.YesNo) {
          showInputDataRequiredYesNoDialog({
            message: title,
            submessage: text,
            onAccept: onAcceptString,
            onCancel,
            inputName: rule.input?.inputName,
          });
        } else if (type === DataInputs.Text) {
          showInputDataRequiredDialog({
            type,
            caption: title,
            message: text,
            onAccept: onAcceptString,
            onCancel,
            inputName: rule.input?.inputName,
            inputLabel: rule.input?.inputLabel,
            fullScreen: true,
          });
        } else {
          showInputDataRequiredDialog({
            type,
            caption: title,
            message: text,
            onAccept: onAcceptString,
            onCancel,
            inputName: rule.input?.inputName,
            inputLabel: rule.input?.inputLabel,
          });
        }
        break;
      }
      case BrmActionType.NotificationAction: {
        dispatchNotification(rule).then(runApproval);
        break;
      }
      case BrmActionType.ConfirmationAction: {
        if (rule.restrictionType === RestrictionType.PromotionOffline) {
          showEmeraldDialog({
            type: EmeraldDialogTypes.Info,
            size: MessageModalSizeEnum.Large,
            message: 'PromotionEngineOfflineMessage',
            subMessage: 'PromotionEngineOfflineSubMessage',
            subMessage2: 'PromotionEngineOfflineSubMessage2',
            onAccept: runApproval,
            onCancel: () => {
              if (
                store.getState().transaction.cartStatus &&
                store.getState().transaction.cartStatus === CartStatus.Recall
              ) {
                runApproval();
                voidTransactionWhenUEOffline.setIsVoid(true);
              } else {
                onCancel();
              }
            },
            acceptButtonLabel: 'Continue',
            closeButtonLabel: 'Void_Transaction_Command',
            separateButton: true,
          });
        } else {
          showEmeraldDialog({
            message: title,
            type: EmeraldDialogTypes.InfoDecision,
            subMessage: text,
            onAccept: runApproval,
            onCancel,
            acceptButtonLabel: 'OK',
          });
        }
        break;
      }
      default: {
        throw new EmeraldError('NotImplemented', `No Modal for BRM Type ${rule.type}`);
      }
    }
  });
};
